import React, { createContext, useContext, useCallback, useEffect, useState, ReactNode } from "react";
import { debounce } from "lodash";
import { NotificationPreviewMontageDone, NotificationPreviewMontageError, NotificationTranscriptionDone, NotificationTranscriptionError, Template } from "chq";
import { makeGet, makePost, makePatch, makeDelete } from "@culturehq/client";

import { useProfile } from "app/context";
import { useFetch, useNotificationReceivedListener, usePagination } from "app/hooks";
import { errorService, t } from "app/utils";

import notificationsTypes from "constants/notificationsTypes";
import downloadFile from "app/utils/downloadFile";
import { createEntity, deleteEntity, updateEntity } from "components/admin/settings/mutations";
import { StateProps } from "../utils/BuilderTemplateTypes";

const createTemplate = createEntity("templates");
const updateTemplate = updateEntity("templates");
const deleteTemplate = deleteEntity("templates");

interface DynamicElement {
  elementId: string;
  type: string;
}

interface RelativeElement {
  elementId: string;
  relativeToId: string;
}

export type TemplateType = "none" | "image" | "video" | "montage";

interface TemplateBuilderContextType {
  state: StateProps;
  pagination: any;
  onPageClick: (nextPage: number) => void;
  templateBuilderSelection: TemplateType;
  dynamicElements: DynamicElement[];
  relativeElements: RelativeElement[];
  setDynamicElements: React.Dispatch<React.SetStateAction<DynamicElement[]>>;
  setRelativeElements: React.Dispatch<React.SetStateAction<RelativeElement[]>>;
  startTemplateBuilder: (type: TemplateType, template?: Template) => void;
  handleCreate: (template: any) => void;
  handleUpdate: (template: Template) => Promise<void>;
  handleDelete: ({ id, value }: Template) => void;
  handleSort: (updatedTemplates: Template[]) => void;
  handleSave: (template: Partial<Template>) => Promise<void>;
  handleState: (_props: Partial<StateProps>) => void;
  restartContextValues: () => void;
}

const initialState = {
  template: undefined,
  templates: [],
  loading: false,
  progress: 0,
  error: undefined,
  title: "",
  templateId: undefined
};

const TemplateBuilderContext = createContext<TemplateBuilderContextType>({
  state: initialState,
  pagination: {},
  onPageClick: (_nextPage: number) => {},
  templateBuilderSelection: "none",
  dynamicElements: [],
  relativeElements: [],
  setDynamicElements: () => {},
  setRelativeElements: () => {},
  startTemplateBuilder: (_type: TemplateType) => {},
  handleCreate: (_template: any) => {},
  handleUpdate: (_template: Template) => Promise.resolve(),
  handleDelete: (_template: Template) => {},
  handleSort: (_updatedTemplates: Template[]) => {},
  handleState: (_props: Partial<StateProps>) => {},
  handleSave: (_template: Partial<Template>) => Promise.resolve(),
  restartContextValues: () => {}
});

export const TemplateBuilderProvider = (
  { children }: { children: ReactNode }
) => {
  const { profile } = useProfile();
  const [page, onPageClick] = usePagination();
  const [templateBuilderSelection, setTemplateBuilderSelection] = useState<TemplateType>("none");
  const [dynamicElements, setDynamicElements] = useState<DynamicElement[]>([]);
  const [relativeElements, setRelativeElements] = useState<RelativeElement[]>([]);
  const [state, setState] = useState<StateProps>(initialState);

  const handleState = (
    newProps: Partial<StateProps>
  ) => setState(prev => ({ ...prev, ...newProps }));

  const restartContextValues = () => {
    setTemplateBuilderSelection("none");
    setDynamicElements([]);
    setRelativeElements([]);
    handleState({ template: undefined, title: "", templateId: undefined, progress: 0, error: undefined, loading: false });
  };

  const {
    fetching,
    json: { pagination, templates = [] },
    onUpdate: onTemplatesUpdate
  } = useFetch(() => makeGet("/creatomate_templates?manage=true", { page }), [page]);

  useEffect(
    () => {
      handleState({ templates, loading: fetching });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetching, JSON.stringify(templates)]
  );

  const handleCreate = async (template: any) => {
    const json = await makePost<{ template: Template }>("/creatomate_templates", {
      name: template.name,
      templateType: template.type?.toLowerCase() as string,
      description: template.description,
      templateId: template.value,
      thumbnail: template.posterUrl as string,
      mediaUrl: template.url,
      width: template.width,
      height: template.height,
      creatomateJson: template.creatomateJson,
      status: template.status ?? "draft"
    });
    onTemplatesUpdate(createTemplate(json.template));
  };

  const handleUpdate = async (template: Template) => {
    const json = await makePatch<{ template: Template }>(
      `/creatomate_templates/${template.id}`,
      { ...template, templateId: template.value } as any
    );
    onTemplatesUpdate(updateTemplate(json.template));
  };

  const handleDelete = async (template: Template) => {
    await makeDelete(`/creatomate_templates/${template.id || template.value}`);
    onTemplatesUpdate(deleteTemplate(template));
  };

  const handleSort = useCallback(debounce(async (updatedTemplates: Template[]) => {
    handleState({ templates: updatedTemplates });

    const minSort = Math.min(...updatedTemplates.map(t => t.order as number));
    const promises = [];
    for (let i = 0; i < updatedTemplates.length; i += 1) {
      const template = updatedTemplates[i];
      promises.push(makePatch(`/creatomate_templates/${template.id}`,
            { id: template.id, sort: minSort + i } as any
      ));
    }
    await Promise.all(promises);
  }, 3000), []);

  const handleSave = async (
    params: Partial<Template>
  ) => {
    if (state.template) {
      return handleUpdate({
        ...state.template,
        ...params,
        value: state.templateId || state.template?.value,
        name: state.title || state.template?.name
      });
    }

    return handleCreate({
      name: state.title,
      type: templateBuilderSelection,
      value: state.templateId,
      creatomateJson: params.creatomateJson,
      status: params.status
    });
  };

  const startTemplateBuilder = (type: TemplateType, template?: Template) => {
    setTemplateBuilderSelection(type);
    handleState({ template, title: template?.name || state.title });
  };

  useNotificationReceivedListener(
    useCallback(
      async (
        notification: NotificationPreviewMontageError | NotificationPreviewMontageDone
          | NotificationTranscriptionDone | NotificationTranscriptionError
      ) => {
        switch (notification.notificationType) {
          case notificationsTypes.PREVIEW_MONTAGE_ERROR: {
            errorService.error(`Error with preview montage ${profile.id}`);
            handleState({
              loading: false,
              progress: 0,
              error: t("Something went wrong, please try again or contact support") }
            );
            break;
          }
          case notificationsTypes.PREVIEW_MONTAGE_DONE: {
            const previewMontage = notification as NotificationPreviewMontageDone;
            const additionalData = JSON.parse(
              previewMontage.entity.additionalData as unknown as string
            );
            const mediaType = additionalData.output_format === "png" ? "image" : "video";
            downloadFile(previewMontage.entity.url, mediaType);
            handleState({ loading: false, progress: 0 });
            break;
          }
          default:
            break;
        }
      },
      [profile.id]
    )
  );

  const contextValue = {
    state,
    pagination,
    onPageClick,
    templateBuilderSelection,
    dynamicElements,
    relativeElements,
    startTemplateBuilder,
    setDynamicElements,
    setRelativeElements,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleState,
    handleSort,
    handleSave,
    restartContextValues
  };

  return (
    <TemplateBuilderContext.Provider value={contextValue}>
      {children}
    </TemplateBuilderContext.Provider>
  );
};

export const useTemplateBuilder = () => {
  const context = useContext(TemplateBuilderContext);
  return context;
};
